import {
  CompetitionGenderEnum,
  PlaySessionMatchCompetitivenessEnum,
  PlayerFinderTimeOfDayEnum,
  SkillLevelValuesEnum,
  SportsEnum,
} from 'types/generated/client';
import { isWithinPriceRange } from 'utils/shared/money/isWithinPriceRange';
import type { AvailabilityDay, TimesByDay } from 'utils/shared/playerAvailability';
import { formatISODateInTimezone, getHourInTimezone } from 'utils/shared/time/formatEventDateTime';
import { Weekday } from 'screens/ListAvailability/types';
import { weekdayPluralLabel } from 'screens/ListAvailability/utils';
import type { VenueAddressWithCity } from 'screens/ListGame/types';
import { SPORT_OPTIONS } from 'screens/ListGame/types';
import type { Neighborhood } from 'components/Find/Neighborhoods';
import { GAME_FORMAT_OPTIONS, type GameFormat, getGameFormat } from 'components/Find/gameFormat';
import type { FindCourtType, FindGame, FindPlayer } from 'components/Find/types';
import { FIND_RADIUS_MILES, distanceInMiles, neighborhoodCenter } from './geo';

/**
 * The /find filters model + pure apply/sort helpers. Both tabs edit ONE staged
 * `FindFilters` bag (a superset — games ignore `daysOfWeek`, players ignore
 * `date`/price); the tab-specific `applyGameFilters` / `applyPlayerFilters` read
 * only the fields that apply. Everything runs client-side over the already-fetched
 * lists (memoised by the caller) — no extra queries.
 */

export type FindFilterVariant = 'games' | 'players' | 'home';

export type GameSortKey =
  | 'NEAREST_TIME'
  | 'PLAYER_RATING'
  | 'PRICE_LOW_HIGH'
  | 'PRICE_HIGH_LOW'
  | 'SKILL_LEVEL'
  | 'DISTANCE';

export type PlayerSortKey = 'DISTANCE' | 'PLAYER_RATING';

export type FindSortKey = GameSortKey | PlayerSortKey;

export interface FindFilters {
  /** Miles added on top of the base search radius (single-select: 0 | 10 | 20). */
  distanceBonusMiles: number;
  /** Set by the page-level sport control (not the sheet); null = all sports. */
  sport: SportsEnum | null;
  /** Games only. A `yyyy-MM-dd` local day, or null for any day. */
  date: string | null;
  /** Players only. Empty = any day. */
  daysOfWeek: Weekday[];
  /** Empty = all times. */
  timesOfDay: PlayerFinderTimeOfDayEnum[];
  sortBy: FindSortKey;
  /** Empty = all match types. */
  formats: GameFormat[];
  /** Empty = all game types. */
  competitiveness: PlaySessionMatchCompetitivenessEnum[];
  /** Empty = all audiences. */
  genders: CompetitionGenderEnum[];
  /** Empty = any skill. */
  skillLevels: SkillLevelValuesEnum[];
  /** Games only. Major currency units. */
  priceMin: number;
  priceMax: number;
  /** Empty = all court types. */
  courtTypes: FindCourtType[];
  // --- Home sheet only (see screens/PlayerHomeV3/DECISIONS-HOME-FEED.md §4) ---
  /** Youth programming toggle → intended age group YOUTH / ALL_AGES. */
  youthOnly: boolean;
  /** Recurring pattern: chosen day chips + per-day time buckets. Empty = any. */
  availabilityDays: AvailabilityDay[];
  timesByDay: TimesByDay;
  /** Venue whitelist. Non-empty = only items at these venues; radius ignored. */
  venues: VenueAddressWithCity[];
  /** Neighborhood whitelist (mobile "Location"). Non-empty = only items inside one of them. */
  neighborhoods: Neighborhood[];
}

// --- Constants ----------------------------------------------------------------

/** The distance filter widens the base "near you" radius by this much at most, so
 *  the list query fetches at `base + MAX` once and distance filters client-side —
 *  keeping the live "Show N" count accurate with no refetch. */
export const MAX_DISTANCE_BONUS_MILES = 20;
export const FIND_FETCH_RADIUS_MILES = FIND_RADIUS_MILES + MAX_DISTANCE_BONUS_MILES;

export const PRICE_MIN = 0;
export const PRICE_MAX = 50;

export const DEFAULT_GAME_SORT: GameSortKey = 'NEAREST_TIME';
export const DEFAULT_PLAYER_SORT: PlayerSortKey = 'DISTANCE';

/** Home sheet chips are +5/+10/+20 with no "+0", so one is always active. */
export const HOME_DEFAULT_DISTANCE_BONUS_MILES = 5;

export const makeDefaultFilters = (variant: FindFilterVariant): FindFilters => ({
  distanceBonusMiles: variant === 'home' ? HOME_DEFAULT_DISTANCE_BONUS_MILES : 0,
  sport: null,
  date: null,
  daysOfWeek: [],
  timesOfDay: [],
  sortBy: variant === 'games' ? DEFAULT_GAME_SORT : DEFAULT_PLAYER_SORT,
  formats: [],
  competitiveness: [],
  genders: [],
  skillLevels: [],
  priceMin: PRICE_MIN,
  priceMax: PRICE_MAX,
  courtTypes: [],
  youthOnly: false,
  availabilityDays: [],
  timesByDay: {},
  venues: [],
  neighborhoods: [],
});

interface Option<T> {
  label: string;
  value: T;
  iconSrc?: string;
}

/** Value stays the bonus; the label shows the resulting total radius. */
export const DISTANCE_OPTIONS: Option<number>[] = [0, 10, MAX_DISTANCE_BONUS_MILES].map(
  (bonus) => ({ label: `${FIND_RADIUS_MILES + bonus} miles`, value: bonus }),
);

/** Home sheet: "+5 / +10 / +20 miles" on top of the base radius. */
export const HOME_DISTANCE_OPTIONS: Option<number>[] = [5, 10, MAX_DISTANCE_BONUS_MILES].map(
  (bonus) => ({ label: `+${bonus} miles`, value: bonus }),
);

/** Reuse the create-screen sport + match-type chips (same labels + icons). */
export const FILTER_SPORT_OPTIONS = SPORT_OPTIONS;
export const TYPE_OPTIONS = GAME_FORMAT_OPTIONS;

export const TIME_OPTIONS: Option<PlayerFinderTimeOfDayEnum>[] = [
  { label: 'Morning', value: PlayerFinderTimeOfDayEnum.Morning },
  { label: 'Afternoon', value: PlayerFinderTimeOfDayEnum.Afternoon },
  { label: 'Evening', value: PlayerFinderTimeOfDayEnum.Evening },
];

/** Availability slots store the weekday as the local `Weekday` enum (`MON`…), not
 *  the codegen `DayOfWeekEnum` (`MONDAY`…) — that enum backs a different column. */
export const DAY_OPTIONS: Option<Weekday>[] = [
  Weekday.Sun,
  Weekday.Mon,
  Weekday.Tue,
  Weekday.Wed,
  Weekday.Thu,
  Weekday.Fri,
  Weekday.Sat,
].map((value) => ({ label: weekdayPluralLabel(value), value }));

export const GAME_TYPE_OPTIONS: Option<PlaySessionMatchCompetitivenessEnum>[] = [
  { label: 'Recreational play', value: PlaySessionMatchCompetitivenessEnum.Casual },
  { label: 'Competitive', value: PlaySessionMatchCompetitivenessEnum.Match },
];

/** Audience genders that games/availabilities actually carry. "Nonbinary" from the
 *  Figma is omitted: game audience (`CompetitionGenderEnum`) has no nonbinary value,
 *  and player gender isn't fetched — surfacing it needs a permitted `userProfile`
 *  add first. Kept honest rather than showing a chip that can never match. */
export const GENDER_OPTIONS: Option<CompetitionGenderEnum>[] = [
  { label: 'Men', value: CompetitionGenderEnum.Male },
  { label: 'Women', value: CompetitionGenderEnum.Female },
];

export const SKILL_OPTIONS: Option<SkillLevelValuesEnum>[] = [
  { label: 'Brand new', value: SkillLevelValuesEnum.BrandNew },
  { label: 'Beginner', value: SkillLevelValuesEnum.Beginner },
  { label: 'Intermediate', value: SkillLevelValuesEnum.Intermediate },
  { label: 'Advanced', value: SkillLevelValuesEnum.Advanced },
  { label: 'Pro', value: SkillLevelValuesEnum.Pro },
];

export const COURT_TYPE_OPTIONS: Option<FindCourtType>[] = [
  { label: 'Indoor', value: 'INDOOR' },
  { label: 'Outdoor', value: 'OUTDOOR' },
];

export const GAME_SORT_OPTIONS: Option<GameSortKey>[] = [
  { label: 'Nearest time', value: 'NEAREST_TIME' },
  { label: 'Player rating', value: 'PLAYER_RATING' },
  { label: 'Price Low-High', value: 'PRICE_LOW_HIGH' },
  { label: 'Price High-Low', value: 'PRICE_HIGH_LOW' },
  { label: 'Skill level', value: 'SKILL_LEVEL' },
  { label: 'Distance', value: 'DISTANCE' },
];

export const PLAYER_SORT_OPTIONS: Option<PlayerSortKey>[] = [
  { label: 'Distance', value: 'DISTANCE' },
  { label: 'Player rating', value: 'PLAYER_RATING' },
];

// --- Derive helpers (shared by the list mappers) ------------------------------

/** The time-of-day bucket a local hour (0–23) falls in. */
export const timeOfDayFromHour = (hour: number): PlayerFinderTimeOfDayEnum => {
  if (hour >= 5 && hour < 12) return PlayerFinderTimeOfDayEnum.Morning;
  if (hour >= 12 && hour < 17) return PlayerFinderTimeOfDayEnum.Afternoon;
  return PlayerFinderTimeOfDayEnum.Evening;
};

/** Indoor/outdoor court surfacing from a venue's court counts. */
export const deriveCourtTypes = (indoorCount: number, outdoorCount: number): FindCourtType[] => {
  const types: FindCourtType[] = [];
  if (indoorCount > 0) types.push('INDOOR');
  if (outdoorCount > 0) types.push('OUTDOOR');
  return types;
};

// --- Filtering ----------------------------------------------------------------

/** Ascending skill rank for sorting; unknown levels sort last. */
const SKILL_RANK: Partial<Record<SkillLevelValuesEnum, number>> = {
  [SkillLevelValuesEnum.BrandNew]: 0,
  [SkillLevelValuesEnum.Beginner]: 1,
  [SkillLevelValuesEnum.Intermediate]: 2,
  [SkillLevelValuesEnum.Advanced]: 3,
  [SkillLevelValuesEnum.Pro]: 4,
};

/** Hour of day at the venue (browser-local when the zone is unknown). */
const localHour = (iso: string, timezone?: string): number => getHourInTimezone(iso, timezone);

/** yyyy-MM-dd at the venue (browser-local when the zone is unknown), matching a native
 *  `<input type="date">` value. */
const localDayKey = (iso: string, timezone?: string): string =>
  formatISODateInTimezone(iso, timezone);

/**
 * Measured from the search centre, not the viewer: the cards show how far something is
 * from you, but the slider narrows the area being searched. Picked neighborhoods
 * become that centre and bound the area themselves, so the slider stops applying.
 */
const withinRadius = (searchDistanceMiles: number | null, f: FindFilters): boolean =>
  f.neighborhoods.length > 0 ||
  searchDistanceMiles == null ||
  searchDistanceMiles <= FIND_RADIUS_MILES + f.distanceBonusMiles;

/** Inside any selected neighborhood's radius, or (players) listing one of them by id. */
export const matchesNeighborhoods = (
  item: { latitude?: number; longitude?: number; neighborhoods?: { id: string }[] },
  selected: Neighborhood[],
): boolean => {
  if (selected.length === 0) return true;
  if (item.neighborhoods?.some((n) => selected.some((s) => s.id === n.id))) return true;
  if (item.latitude == null || item.longitude == null) return false;
  const point = { lat: item.latitude, lng: item.longitude };
  return selected.some(
    (s) => distanceInMiles(point, neighborhoodCenter(s)) <= Number(s.radiusMiles),
  );
};

export const withinPriceRange = (
  priceMajorUnits: number,
  { priceMin, priceMax }: Pick<FindFilters, 'priceMin' | 'priceMax'>,
): boolean =>
  isWithinPriceRange(priceMajorUnits, {
    minimum: priceMin,
    maximum: priceMax,
    sliderMaximum: PRICE_MAX,
  });

const matchesGame = (game: FindGame, f: FindFilters): boolean => {
  if (!withinRadius(game.searchDistanceMiles, f)) return false;
  if (!matchesNeighborhoods(game, f.neighborhoods)) return false;
  if (f.sport && game.sport !== f.sport) return false;
  if (f.date && localDayKey(game.startDateTime, game.timezone) !== f.date) return false;
  if (
    f.timesOfDay.length &&
    !f.timesOfDay.includes(timeOfDayFromHour(localHour(game.startDateTime, game.timezone)))
  )
    return false;
  if (f.formats.length && !f.formats.includes(game.format)) return false;
  if (
    f.competitiveness.length &&
    (!game.competitiveness || !f.competitiveness.includes(game.competitiveness))
  )
    return false;
  if (f.genders.length && !f.genders.includes(game.gender)) return false;
  if (f.skillLevels.length && !(game.skillLevel && f.skillLevels.includes(game.skillLevel)))
    return false;
  if (!withinPriceRange(game.priceFrom ?? 0, f)) return false;
  if (f.courtTypes.length && !game.courtTypes.some((ct) => f.courtTypes.includes(ct))) return false;
  return true;
};

/** Miles from the search centre to the player's closest listed venue. */
export const playerNearestVenue = (player: FindPlayer): number | null =>
  player.venues.reduce<number | null>((min, v) => {
    if (v.searchDistanceMiles == null) return min;
    return min == null ? v.searchDistanceMiles : Math.min(min, v.searchDistanceMiles);
  }, null);

const matchesPlayer = (player: FindPlayer, f: FindFilters): boolean => {
  if (!withinRadius(playerNearestVenue(player), f)) return false;
  if (!matchesNeighborhoods(player, f.neighborhoods)) return false;
  if (f.sport && player.sport !== f.sport) return false;
  if (f.daysOfWeek.length && !player.availabilityDays.some((d) => f.daysOfWeek.includes(d)))
    return false;
  if (f.timesOfDay.length && !player.availabilityTimes.some((t) => f.timesOfDay.includes(t)))
    return false;
  if (f.formats.length && !f.formats.includes(getGameFormat(player.teamType))) return false;
  if (f.competitiveness.length && !f.competitiveness.includes(player.competitiveness)) return false;
  if (f.genders.length && !f.genders.includes(player.partnerGender)) return false;
  if (f.skillLevels.length && !(player.skillLevel && f.skillLevels.includes(player.skillLevel)))
    return false;
  if (f.courtTypes.length && !player.courtTypes.some((ct) => f.courtTypes.includes(ct)))
    return false;
  return true;
};

// --- Sorting ------------------------------------------------------------------

const skillRank = (level?: SkillLevelValuesEnum): number =>
  level != null && SKILL_RANK[level] != null ? (SKILL_RANK[level] as number) : -1;

/** Null distances sort last regardless of direction. */
const byDistance = (a: number | null, b: number | null): number => {
  if (a == null && b == null) return 0;
  if (a == null) return 1;
  if (b == null) return -1;
  return a - b;
};

const sortGames = (games: FindGame[], sortBy: FindSortKey): FindGame[] => {
  const sorted = [...games];
  switch (sortBy) {
    case 'PRICE_LOW_HIGH':
      return sorted.sort((a, b) => (a.priceFrom ?? 0) - (b.priceFrom ?? 0));
    case 'PRICE_HIGH_LOW':
      return sorted.sort((a, b) => (b.priceFrom ?? 0) - (a.priceFrom ?? 0));
    case 'DISTANCE':
      return sorted.sort((a, b) => byDistance(a.searchDistanceMiles, b.searchDistanceMiles));
    case 'PLAYER_RATING':
      return sorted.sort((a, b) => skillRank(b.skillLevel) - skillRank(a.skillLevel));
    case 'SKILL_LEVEL':
      return sorted.sort((a, b) => skillRank(a.skillLevel) - skillRank(b.skillLevel));
    case 'NEAREST_TIME':
    default:
      return sorted.sort(
        (a, b) => new Date(a.startDateTime).getTime() - new Date(b.startDateTime).getTime(),
      );
  }
};

const sortPlayers = (players: FindPlayer[], sortBy: FindSortKey): FindPlayer[] => {
  const sorted = [...players];
  if (sortBy === 'PLAYER_RATING') {
    return sorted.sort((a, b) => skillRank(b.skillLevel) - skillRank(a.skillLevel));
  }
  // DISTANCE (default)
  return sorted.sort((a, b) => byDistance(playerNearestVenue(a), playerNearestVenue(b)));
};

// --- Public apply -------------------------------------------------------------

export const applyGameFilters = (games: FindGame[], filters: FindFilters): FindGame[] =>
  sortGames(
    games.filter((game) => matchesGame(game, filters)),
    filters.sortBy,
  );

export const applyPlayerFilters = (players: FindPlayer[], filters: FindFilters): FindPlayer[] =>
  sortPlayers(
    players.filter((player) => matchesPlayer(player, filters)),
    filters.sortBy,
  );

/** How many filter facets are narrowing results — drives the DiscoveryBar dot.
 *  Sort and distance-bonus 0 don't count (they don't reduce the set).
 *  `excludeLocation`: from lg the neighborhoods live in the header pill, not the sheet. */
export const countActiveFilters = (
  filters: FindFilters,
  variant: FindFilterVariant,
  { excludeLocation = false } = {},
): number => {
  let count = 0;
  const defaultBonus = variant === 'home' ? HOME_DEFAULT_DISTANCE_BONUS_MILES : 0;
  if (filters.distanceBonusMiles !== defaultBonus) count += 1;
  if (filters.neighborhoods.length && !excludeLocation) count += 1;
  if (filters.timesOfDay.length) count += 1;
  if (filters.formats.length) count += 1;
  if (filters.competitiveness.length) count += 1;
  if (filters.genders.length) count += 1;
  if (filters.skillLevels.length) count += 1;
  if (filters.courtTypes.length) count += 1;
  if (variant === 'games') {
    if (filters.date) count += 1;
    if (filters.priceMin > PRICE_MIN || filters.priceMax !== PRICE_MAX) count += 1;
  } else if (variant === 'home') {
    if (filters.priceMin > PRICE_MIN || filters.priceMax !== PRICE_MAX) count += 1;
    if (filters.youthOnly) count += 1;
    if (
      filters.availabilityDays.length ||
      Object.values(filters.timesByDay).some((times) => times && times.length > 0)
    )
      count += 1;
    if (filters.venues.length) count += 1;
  } else if (filters.daysOfWeek.length) {
    count += 1;
  }
  return count;
};
